import { isValidPhoneHelper } from '@elfsight/lib-utils';
import { AsYouType, validatePhoneNumberLength } from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/types';

export function restoreCursor(
  inputElement: HTMLInputElement | HTMLTextAreaElement | undefined | null,
  position: number
) {
  if (!inputElement) {
    return;
  }

  requestAnimationFrame(() => {
    try {
      if (document.activeElement !== inputElement) {
        inputElement.focus({ preventScroll: true });
      }
    } catch {
      if (document.activeElement !== inputElement) {
        inputElement.focus();
      }
    }
    const clampedPosition = Math.max(
      0,
      Math.min(position, inputElement.value.length)
    );
    inputElement.setSelectionRange(clampedPosition, clampedPosition);
  });
}

export function isEmptyOrRemovedPlusPrefix(
  currentInput: string,
  previousInput: string
) {
  const removedPlus =
    previousInput.startsWith('+') && !currentInput.startsWith('+');
  const cleared = currentInput === '' || currentInput === '+';
  return removedPlus || cleared;
}

interface GetNewCursorPositionArgs {
  rawInput: string;
  rawCursorIndex: number;
  formattedInput: string;
}

export function getNewCursorPosition({
  rawInput,
  rawCursorIndex,
  formattedInput
}: GetNewCursorPositionArgs) {
  const digitsBeforeCursor = (
    rawInput.slice(0, rawCursorIndex).match(/\d/g) || []
  ).length;
  let newPosition = 0;
  let digitsSeen = 0;
  while (
    newPosition < formattedInput.length &&
    digitsSeen < digitsBeforeCursor
  ) {
    if (/\d/.test(formattedInput[newPosition])) {
      digitsSeen++;
    }

    newPosition++;
  }

  if (digitsBeforeCursor === 0 && formattedInput.startsWith('+')) {
    const plusIndex = formattedInput.indexOf('+');
    newPosition = Math.min(formattedInput.length, plusIndex + 1);
  }

  return newPosition;
}

export function isPhonePossible(phone: string) {
  const isValid = isValidPhoneHelper(phone);
  const lengthValidationResult = validatePhoneNumberLength(phone);

  return (
    isValid ||
    lengthValidationResult === 'TOO_SHORT' ||
    lengthValidationResult === 'INVALID_LENGTH'
  );
}

export const formatPhonePrefix = (callingCode: string) => `+${callingCode}`;

export function prependPlusIfNeeded(
  currentInput: string,
  previousInput: string
): {
  currentInput: string;
  cursorOffset: number;
} {
  const isPasteNumberInEmptyInput =
    previousInput.length === 0 &&
    !currentInput.startsWith('+') &&
    currentInput.length > 0;

  if (isPasteNumberInEmptyInput) {
    return {
      currentInput: '+' + currentInput,
      cursorOffset: currentInput.length
    };
  }

  const isAttemptingToRemoveLeadingPlus =
    previousInput.startsWith('+') &&
    !currentInput.startsWith('+') &&
    currentInput.length > 0;

  const isStartingWithSingleDigit =
    currentInput.length === 1 && /^\d$/.test(currentInput[0]);

  if (isStartingWithSingleDigit || isAttemptingToRemoveLeadingPlus) {
    return { currentInput: '+' + currentInput, cursorOffset: 1 };
  }
  return { currentInput, cursorOffset: 0 };
}

export function getFormattedPhone(value: string, countryCode: CountryCode) {
  const phoneFormatter = new AsYouType(countryCode);
  return phoneFormatter.input(value);
}
